// Shared dashboard primitives (page title, range toggle, stat card, charts, formatters)
import { useState } from "react";

// ---- safe formatters (prevent toLocaleString crashes on undefined/null) ----
export const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};
export const fmtKr = (v) => `${safeNum(v).toLocaleString("da-DK")} kr.`;
export const fmtInt = (v) => safeNum(v).toLocaleString("da-DK");
export const fmtHours = (v) => `${safeNum(v)}t`;
export const orDash = (v) => (v === null || v === undefined || v === "" ? "—" : v);

export function PageHeader({ title, subtitle, right }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
                {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
            </div>
            {right}
        </div>
    );
}

export const RANGE_OPTIONS = [
    { v: "week", label: "Uge" },
    { v: "month", label: "Måned" },
    { v: "year", label: "År" },
    { v: "total", label: "Total" },
];

export function RangeToggle({ value, onChange, testid = "range-toggle", showTotal = true }) {
    const options = showTotal ? RANGE_OPTIONS : RANGE_OPTIONS.filter((o) => o.v !== "total");
    return (
        <div data-testid={testid} className="inline-flex bg-white border border-slate-200 rounded-full p-1 shadow-sm">
            {options.map((o) => (
                <button
                    key={o.v}
                    data-testid={`${testid}-${o.v}`}
                    onClick={() => onChange(o.v)}
                    className={`px-3.5 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                        value === o.v ? "bg-blue-600 text-white shadow" : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

export function StatCard({ label, value, hint, accent = "blue", icon: Icon, testid }) {
    const accents = {
        blue: "from-blue-500/10 to-blue-600/5 text-blue-600",
        emerald: "from-emerald-500/10 to-emerald-600/5 text-emerald-600",
        purple: "from-purple-500/10 to-purple-600/5 text-purple-600",
        orange: "from-orange-500/10 to-orange-600/5 text-orange-600",
    };
    return (
        <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
                {Icon && (
                    <span className={`w-9 h-9 rounded-xl bg-gradient-to-br ${accents[accent]} inline-flex items-center justify-center`}>
                        <Icon className="w-4 h-4" />
                    </span>
                )}
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-3">{value ?? 0}</p>
            {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
        </div>
    );
}

export function StatusPill({ status }) {
    const map = {
        "Færdig": "bg-emerald-50 text-emerald-700 border-emerald-100",
        "Afsluttet": "bg-emerald-50 text-emerald-700 border-emerald-100",
        "I gang": "bg-blue-50 text-blue-700 border-blue-100",
        "Aktiv": "bg-blue-50 text-blue-700 border-blue-100",
        "Klar": "bg-amber-50 text-amber-700 border-amber-100",
        "Sendt": "bg-blue-50 text-blue-700 border-blue-100",
        "Betalt": "bg-emerald-50 text-emerald-700 border-emerald-100",
        "Pause": "bg-slate-100 text-slate-600 border-slate-200",
        "Tilgængelig": "bg-emerald-50 text-emerald-700 border-emerald-100",
        "Sygemeldt": "bg-red-50 text-red-700 border-red-100",
        "Ferie": "bg-purple-50 text-purple-700 border-purple-100",
        "Note tilføjet": "bg-purple-50 text-purple-700 border-purple-100",
    };
    return (
        <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full border ${map[status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
            {status || "Ukendt"}
        </span>
    );
}

export function MiniBarChart({ data, color = "#3b82f6" }) {
    if (!data || !data.length) return null;
    const max = Math.max(...data);
    const w = 360, h = 110, gap = 4;
    const barW = (w - gap * (data.length - 1)) / data.length;
    return (
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32" aria-hidden="true">
            {data.map((v, i) => {
                const barH = max === 0 ? 0 : (v / max) * (h - 16);
                const x = i * (barW + gap);
                const y = h - barH;
                return (
                    <rect key={i} x={x} y={y} width={barW} height={barH} rx="3" fill={color} opacity={0.85} />
                );
            })}
        </svg>
    );
}

// Multi-series line chart (responsive SVG, no dependency)
export function MultiLineChart({ series = [], labels = [], height = 220 }) {
    const valid = series.filter((s) => s && s.data && s.data.length);
    if (!valid.length || !labels.length) {
        return <div className="h-44 flex items-center justify-center text-slate-400 text-sm">Ingen data for valgt periode</div>;
    }
    const n = labels.length;
    const W = 720, H = height, padL = 36, padR = 16, padT = 14, padB = 28;
    const max = Math.max(1, ...valid.flatMap((s) => s.data.map((v) => safeNum(v))));
    const niceMax = Math.ceil(max * 1.1);
    const xStep = (W - padL - padR) / Math.max(1, n - 1);
    const yFor = (v) => H - padB - (safeNum(v) / niceMax) * (H - padT - padB);
    const yTicks = 4;
    const tickVals = Array.from({ length: yTicks + 1 }, (_, i) => Math.round((niceMax * i) / yTicks));

    return (
        <div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height }}>
                {/* gridlines */}
                {tickVals.map((t, i) => {
                    const y = yFor(t);
                    return (
                        <g key={i}>
                            <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="#eef2f7" strokeWidth="1" />
                            <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="10" fill="#94a3b8">{t}</text>
                        </g>
                    );
                })}
                {/* x labels */}
                {labels.map((l, i) => (
                    <text key={i} x={padL + i * xStep} y={H - 8} textAnchor="middle" fontSize="10" fill="#94a3b8">{l}</text>
                ))}
                {/* lines */}
                {valid.map((s) => {
                    const path = s.data.map((v, i) => `${i === 0 ? "M" : "L"}${padL + i * xStep},${yFor(v)}`).join(" ");
                    return (
                        <g key={s.key}>
                            <path d={path} stroke={s.color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            {s.data.map((v, i) => (
                                <circle key={i} cx={padL + i * xStep} cy={yFor(v)} r="2.5" fill={s.color} />
                            ))}
                        </g>
                    );
                })}
            </svg>
            <div className="flex flex-wrap gap-3 mt-2">
                {valid.map((s) => (
                    <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.label}
                    </span>
                ))}
            </div>
        </div>
    );
}

export function useRange(initial = "week") {
    const [range, setRange] = useState(initial);
    return [range, setRange];
}

// Filter by deadline string (frontend approximation) — used for "Uge / Måned / År / Total"
export function matchRangeDeadline(deadline, range) {
    if (range === "total") return true;
    const d = (deadline || "").toLowerCase();
    if (range === "week") return ["i dag", "i morgen", "i går", "denne uge", "mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lørdag", "søndag"].some((k) => d.includes(k));
    if (range === "month") return true; // assume jobs are within current month by default
    if (range === "year") return true;
    return true;
}
